import { forwardRef } from "react";
import { bool, func, node, number, oneOfType, string } from "prop-types";
import styled, { css } from "styled-components/macro";
import { rem, transparentize } from "polished";

import { colors } from "../Styles.variables";

const controlBorderWidth = ({ shape }) =>
  shape === "primary" ? 0 : rem("1px");

const controlCheckedBgImg = ({ color, shape }) => {
  if (
    shape === "filter-mobile" ||
    shape === "filter-desktop" ||
    shape === "alt"
  ) {
    return (
      'url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2017%22%3E%3Cpath%20stroke-linecap%3D%22square%22%20fill%3D%22' +
      encodeURIComponent(color) +
      '%22%20d%3D%22 M%2021.996094,0.5859375%20C%2017.690812,4.9173781%2013.386812,9.250092%209.0820313,13.582031%206.7225765,11.207761%204.3638777,8.8327404%202.0039063,6.4589844%201.53125,6.9283854%201.0585938,7.3977865%200.5859375,7.8671875%203.4186299,10.716791%206.2499057,13.567802%209.0820313,16.417969%2013.859459,11.610761%2018.636186,6.8028566%2023.414062,1.9960937%2022.941406,1.5260416%2022.46875,1.0559896%2021.996094,0.5859375%20Z %20%22%2F%3E%3C%2Fsvg%3E")'
    );
  } else if (shape === "primary") {
    return (
      'url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%3E%3Cpath%20fill%3D%22' +
      encodeURIComponent(colors.white) +
      '%22%20d%3D%22M3%2C11l2-2%2C5%2C5L21%2C3l2%2C2-13%2C13-7-7Z%22%2F%3E%3C%2Fsvg%3E")'
    );
  } else {
    return (
      'url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2013%2013%22%3E%3Cpath%20fill%3D%22' +
      encodeURIComponent(color) +
      '%22%20d%3D%22M13%200h-.71L6.5%205.79.71%200H0v.71L5.79%206.5%200%2012.29V13h.71L6.5%207.21%2012.29%2013H13v-.71L7.21%206.5%2013%20.71V0z%22%2F%3E%3C%2Fsvg%3E")'
    );
  }
};
const controlCheckedBgSize = ({ shape }) => {
  switch (shape) {
    case "filter-mobile":
      return `${rem("25px")} ${rem("25px")}`;
    case "filter-desktop":
      return `${rem("18px")} ${rem("18px")}`;
    case "primary":
      return "cover";
    default:
      return "auto";
  }
};

const baseStyles = css`
  display: inline-flex;
  position: relative;
  user-select: none;

  input {
    opacity: 0;
    position: absolute;
    z-index: -1;

    &:checked ~ label:after {
      background-image: ${controlCheckedBgImg};
      background-size: ${controlCheckedBgSize};
      opacity: 1;
    }

    &:checked ~ label:before {
      background-color: ${({ backgrcolor }) => backgrcolor};
    }

    &:focus ~ label:before {
      outline: ${rem("1px")} dotted;
      outline-offset: 0.25em;
    }

    &:disabled ~ label {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  label {
    margin-bottom: 0;
    position: relative;
    text-transform: ${({ upperCase }) => (upperCase ? "uppercase" : "none")};
    vertical-align: top;
    white-space: ${({ wrap }) => (wrap ? "normal" : "nowrap")};

    &:after {
      background: no-repeat 50% 50%;
      content: "";
      display: block;
      opacity: 0;
      position: absolute;
      transition: opacity 0.2s ease-in;
    }

    &:before {
      background-color: ${({ shape }) =>
        shape === "primary" ? colors.gray275 : "transparent"};
      ${({ shape }) =>
        shape === "filter-mobile" || shape === "filter-desktop"
          ? `border-color: ${transparentize(0.7, colors.white)};`
          : ""}
      border-style: solid;
      border-width: ${controlBorderWidth};
      content: "";
      display: block;
      pointer-events: none;
      position: absolute;
    }
  }
`;

const smControlOffsetX = "1.333333333em";
const smControlSize = "0.866666667em";
const smControlOffsetY = "0.066666667em";

const smallStyles = css`
  line-height: 1;
  min-height: ${smControlOffsetX};
  padding-left: ${smControlOffsetX};

  label {
    &:after,
    &:before {
      height: ${smControlSize};
      left: -${smControlOffsetX};
      width: ${smControlSize};
      top: ${smControlOffsetY};
    }
  }
`;

const mdControlOffsetX = "2em";
const mdControlSize = "1.5em";
const mdControlOffsetY = "-0.2em";

const mediumStyles = css`
  line-height: 1.25;
  min-height: ${mdControlOffsetX};
  padding-left: ${mdControlOffsetX};

  label {
    &:after,
    &:before {
      height: ${mdControlSize};
      left: -${mdControlOffsetX};
      width: ${mdControlSize};
      top: ${mdControlOffsetY};
    }
  }
`;

const lgControlOffsetX = "2.625em";
const lgControlSize = "2em";
const lgControlOffsetY = "-0.4em";

const largeStyles = css`
  line-height: 1.25;
  min-height: ${lgControlOffsetX};
  padding-left: ${lgControlOffsetX};

  label {
    &:after,
    &:before {
      height: ${lgControlSize};
      left: -${lgControlOffsetX};
      width: ${lgControlSize};
      top: ${lgControlOffsetY};
    }
  }
`;
const Checkbox = styled(
  forwardRef(
    (
      {
        children,
        checked,
        color,
        disabled,
        id,
        onChange,
        type,
        upperCase,
        wrap,
        backgrcolor,
        shape,
        value,
        ...rest
      },
      ref
    ) => (
      <div {...rest}>
        <input
          checked={checked}
          disabled={disabled}
          id={id}
          onChange={onChange}
          ref={ref}
          type="checkbox"
          value={value}
        />
        <label htmlFor={id}>{children}</label>
      </div>
    )
  )
)`
  ${baseStyles};

  ${({ size }) => {
    switch (size) {
      case "large":
        return largeStyles;
      case "medium":
        return mediumStyles;
      case "small":
      default:
        return smallStyles;
    }
  }}
`;

Checkbox.propTypes = {
  checked: bool,
  children: node.isRequired,
  color: string,
  disabled: bool,
  id: string.isRequired,
  onChange: func,
  upperCase: bool,
  wrap: bool,
  backgrcolor: string,
  shape: string,
  value: oneOfType([number, string]),
};

Checkbox.defaultProps = {
  checked: false,
  color: colors.gray800,
  disabled: false,
  upperCase: true,
  wrap: false,
  backgrcolor: colors.black,
  shape: "",
};

export default Checkbox;
